import { Game } from "../../core/Game";
import { toolMountRefusal } from "../../game/tool-rack";
import { benchGroupAt } from "../../game/bench-work/bench-group";
import { withValidSelectedOperation } from "../../game/game-actions/tool-actions";
import { machineKey } from "../../game/Machine";
import { makeToolItem } from "../../game/material-helpers";
import { ToolItem } from "../../game/Materials";
import { handSpaceLeft } from "../../game/Person";
import { TOOL_TYPES, ToolId } from "../../game/Tool";
import { floorMachines, MachineEntity } from "../entities/MachineEntity";
import { Player } from "../entities/Player";
import { findMachineEntity } from "./machine-commands";
import { projectPerson } from "../projection";

/**
 * Mounting and unmounting handheld tools. A loose tool is a physical
 * thing — a MaterialInstance of kind "tool" carried in the arms, dropped
 * into floor piles, hauled in the truck's bed — so the mount/unmount
 * trade happens between the hands and the station's rack. Each command
 * validates against the live entities, then writes onto them,
 * keeping the rail's selected operation valid through
 * `withValidSelectedOperation`. A refusal logs and returns false, and
 * neither command emits a sound.
 */

function player(game: Game): Player {
  return game.entities.getSingleton(Player);
}

/**
 * Mounts a tool the player is carrying into a station's free tool slot,
 * making the tool's operations available there. The tool has to be in
 * hand — there is no shop-wide storage to pull from. Refused while the
 * station is working, like unmounting.
 */
export function mountTool(
  game: Game,
  entity: MachineEntity,
  tool: ToolItem,
): boolean {
  const machine = entity.view();
  if (!player(game).inventory.some((item) => item === tool)) {
    console.warn(`Tried to mount ${tool.toolId} without carrying it`);
    return false;
  }
  const refusal = toolMountRefusal(machine, tool.toolId);
  if (refusal) {
    console.warn(refusal);
    return false;
  }

  const thePlayer = player(game);
  thePlayer.inventory = thePlayer.inventory.filter((item) => item !== tool);
  entity.state = withValidSelectedOperation({
    ...entity.state,
    tools: [...entity.state.tools, tool.toolId],
  });
  game.dispatch("machineStateChanged", { machine: entity });
  game.dispatch("playerChanged", {});
  return true;
}

/**
 * Hangs a tool lying on a station's shelf straight on a rack — the bench
 * view's drag from the shelf tray onto a hook. The tool never passes
 * through the arms, so full hands don't get in the way; the shelf and
 * the rack may be different tables in one bench run. Same guards as
 * `mountTool` on the rack's side.
 */
export function mountStoredTool(
  game: Game,
  from: MachineEntity,
  onto: MachineEntity,
  tool: ToolItem,
): boolean {
  const stored = from.state.storedMaterials ?? [];
  if (!stored.includes(tool)) {
    console.warn(`Tried to mount ${tool.toolId} off a shelf it isn't on`);
    return false;
  }
  const refusal = toolMountRefusal(onto.view(), tool.toolId);
  if (refusal) {
    console.warn(refusal);
    return false;
  }
  from.state = {
    ...from.state,
    storedMaterials: stored.filter((item) => item !== tool),
  };
  onto.state = withValidSelectedOperation({
    ...onto.state,
    tools: [...onto.state.tools, tool.toolId],
  });
  game.dispatch("machineStateChanged", { machine: from });
  if (onto !== from) game.dispatch("machineStateChanged", { machine: onto });
  return true;
}

/** Takes a tool off a station's rack into the player's hands. */
export function unmountTool(
  game: Game,
  entity: MachineEntity,
  toolId: ToolId,
): boolean {
  const toolIndex = entity.state.tools.indexOf(toolId);
  if (toolIndex === -1) {
    console.warn(`Tried to unmount ${toolId} but it's not mounted`);
    return false;
  }
  // Don't yank a tool out from under a running operation
  if (entity.state.operationProgress.status === "inProgress") {
    console.warn("Can't unmount tools while the station is working");
    return false;
  }
  // The tool comes off into the arms, so they need room for it
  if (handSpaceLeft(projectPerson(game)) < 1) {
    console.warn("Can't unmount a tool with full hands");
    return false;
  }

  const thePlayer = player(game);
  thePlayer.inventory = [...thePlayer.inventory, makeToolItem(toolId)];
  entity.state = withValidSelectedOperation({
    ...entity.state,
    tools: [
      ...entity.state.tools.slice(0, toolIndex),
      ...entity.state.tools.slice(toolIndex + 1),
    ],
  });
  game.dispatch("machineStateChanged", { machine: entity });
  game.dispatch("playerChanged", {});
  return true;
}

/**
 * Slides a tool mounted on a neighbouring table in the same bench run
 * onto this one — the tool-rack half of what `gatherBenchPieces` does
 * for stock. Tables pushed together are one bench
 * (bench-work/bench-group.ts) and the rail shows the whole run's tools,
 * but an operation still resolves tools off a single machine's rack, so
 * reaching for a neighbour's tool moves it here first and everything
 * downstream runs as if it had always hung here.
 */
export function gatherBenchTool(
  game: Game,
  target: MachineEntity,
  toolId: ToolId,
): boolean {
  const machine = target.view();
  if (target.state.tools.length >= machine.toolSlots) {
    console.warn(`No free tool slots on ${machine.type.name}`);
    return false;
  }
  const compatible = TOOL_TYPES[toolId].compatibleMachines;
  if (compatible && !compatible.includes(target.state.machineTypeId)) {
    console.warn(`${toolId} doesn't mount on a ${machine.type.name}`);
    return false;
  }
  if (target.state.operationProgress.status === "inProgress") {
    console.warn("Can't mount tools while the station is working");
    return false;
  }

  // Only within the run this table belongs to — a tool two benches over
  // across the shop stays where it hangs.
  const group = benchGroupAt(floorMachines(game), machine);
  const from = group.members
    .map((member) => member.machine)
    .filter(
      (candidate) => machineKey(candidate.state) !== machineKey(machine.state),
    )
    .find(
      (candidate) =>
        candidate.state.tools.includes(toolId) &&
        candidate.state.operationProgress.status !== "inProgress",
    );
  const fromEntity = from ? findMachineEntity(game, from.state) : null;
  if (!fromEntity) {
    console.warn(`No idle table in the run has a ${toolId} to gather`);
    return false;
  }

  const toolIndex = fromEntity.state.tools.indexOf(toolId);
  fromEntity.state = withValidSelectedOperation({
    ...fromEntity.state,
    tools: [
      ...fromEntity.state.tools.slice(0, toolIndex),
      ...fromEntity.state.tools.slice(toolIndex + 1),
    ],
  });
  target.state = withValidSelectedOperation({
    ...target.state,
    tools: [...target.state.tools, toolId],
  });
  game.dispatch("machineStateChanged", { machine: fromEntity });
  game.dispatch("machineStateChanged", { machine: target });
  return true;
}
